import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCanAccess, useDataProvider, useTranslate } from "ra-core";

import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import { FIELD_CLASS, HELP_CLASS } from "../guardias/guardiasShared";
import type { CrmDataProvider } from "../providers/types";
import type { WhatsAppCounterRow } from "../types";
import {
  buildMonthOptions,
  counterLevel,
  usagePercent,
} from "./whatsappCounterRules";

const NUMBER_FORMAT = new Intl.NumberFormat("es-MX");

const CounterRow = ({
  row,
  index,
  total,
}: {
  row: WhatsAppCounterRow;
  index: number;
  total: number;
}) => {
  const translate = useTranslate();
  const level = counterLevel(row.serviceMessages, row.monthlyFreeQuota);
  return (
    <li className="space-y-2" data-testid="whatsapp-counter-row">
      {/* The number is never shown or even held here: rows are anonymous. */}
      <p className="text-base font-semibold">
        {total > 1
          ? translate("crm.whatsapp.counter.number_n", { index: index + 1 })
          : translate("crm.whatsapp.counter.number")}
      </p>
      <p className="text-base tabular-nums">
        {translate("crm.whatsapp.counter.used", {
          used: NUMBER_FORMAT.format(row.serviceMessages),
          quota: NUMBER_FORMAT.format(row.monthlyFreeQuota),
        })}
      </p>
      <Progress
        aria-label={translate("crm.whatsapp.counter.progress_label")}
        value={usagePercent(row.serviceMessages, row.monthlyFreeQuota)}
      />
      {level !== "ok" ? (
        <div role="status">
          <StatusPill
            tone="not-ok"
            text={translate(`crm.whatsapp.counter.${level}`)}
          />
        </div>
      ) : null}
      <p className="text-base tabular-nums">
        {translate("crm.whatsapp.counter.paid_templates", {
          count: NUMBER_FORMAT.format(row.paidTemplates),
        })}
      </p>
    </li>
  );
};

/**
 * Almikia Epic EW2 (TASK-080): the 'Mensajes de atencion del mes' counter. Direccion only (the
 * card renders nothing for anyone else, and the server function rejects them too). One row per
 * business number, labelled generically ('Numero del inbox'): service messages used out of the
 * free quota with a progress bar, a warning from 80 % and at 100 %, and the month total of paid
 * templates. The month is selectable (Mexico City calendar months); the figures are computed by
 * the database in ONE aggregate call. Messages from Pepe's app and templates are not counted as
 * service messages. `now` is injectable for tests.
 */
export const WhatsAppCounterCard = ({
  compact = false,
  now,
}: {
  compact?: boolean;
  now?: Date;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const access = useCanAccess({
    resource: "whatsapp_settings",
    action: "list",
  });
  const [monthKey, setMonthKey] = useState<string | null>(null);
  const options = useMemo(() => buildMonthOptions(now ?? new Date()), [now]);
  const selected =
    options.find((option) => option.key === monthKey) ?? options[0];
  const allowed = !access.isPending && access.canAccess;
  const counter = useQuery({
    queryKey: ["whatsapp", "counter", selected.anchor],
    queryFn: () => dataProvider.getWhatsAppCounter(selected.anchor),
    enabled: allowed,
    retry: false,
  });

  if (!allowed) return null;

  return (
    <Card data-testid="whatsapp-counter-card">
      <CardContent className="space-y-3 p-4">
        <h2
          className={
            compact ? "text-base font-semibold" : "text-lg font-semibold"
          }
        >
          {translate("crm.whatsapp.counter.title")}
        </h2>
        <div className="space-y-1">
          <Label htmlFor="whatsapp-counter-month">
            {translate("crm.whatsapp.counter.month_label")}
          </Label>
          <select
            id="whatsapp-counter-month"
            className={FIELD_CLASS}
            value={selected.key}
            onChange={(event) => setMonthKey(event.target.value)}
          >
            {options.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {counter.error ? (
          <ListErrorState
            message={translate("crm.whatsapp.counter.error")}
            onRetry={() => counter.refetch()}
          />
        ) : counter.data === undefined ? (
          <ListCardsSkeleton rows={1} />
        ) : counter.data.length === 0 ? (
          <p className={HELP_CLASS}>
            {translate("crm.whatsapp.counter.empty")}
          </p>
        ) : (
          <ul className="space-y-4">
            {counter.data.map((row, index) => (
              <CounterRow
                key={index}
                row={row}
                index={index}
                total={counter.data.length}
              />
            ))}
          </ul>
        )}
        <p className={HELP_CLASS}>{translate("crm.whatsapp.counter.note")}</p>
      </CardContent>
    </Card>
  );
};
